import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, Navigation2, Play, Power } from 'lucide-react';
import { VerneMark } from '@verne/brand';
import type { NavigationMapInfo, RobotPose, WaypointMap } from '@shared/lib/api/navigation';
import { RobotMinimap } from '@/components/navigation/robot-minimap';
import { PowerOffOverlay } from '@/components/system/power-off-overlay';
import { useRobotStore } from '@/stores/useRobotStore';
import { useActionError } from '@/stores/useActionError';
import { startNavigation } from '@/lib/navigation-api';
import { startTaskExecution } from '@/lib/task-api';

interface DashboardSidebarProps {
  mapInfo: NavigationMapInfo | null;
  mapUrl: string;
  mapLoading: boolean;
  navPose: RobotPose | null;
  navPath: Array<[number, number]>;
  waypoints: WaypointMap;
  onOpenMap: () => void;
}

function deriveStateLabel(
  state: string | null | undefined,
  baseOwner: string | null | undefined,
  navStatus: string | null | undefined,
  healingInProgress: boolean,
): string {
  if (state === 'navigation' && baseOwner === 'navigation' && navStatus === 'active') {
    return 'Autonomous Navigation';
  }
  if (state === 'navigation' && baseOwner === 'teleop') return 'User is Driving';
  if (state === 'verne_control') return 'Verne Control';
  if (state === 'navigation') return 'Navigation';
  if (state === 'task_execution') return 'Task Execution';
  if (state === 'orchestrator_control') return 'Orchestrator Control';
  if (state === 'idle') return 'Idle';
  if (state === 'booting') return 'Booting';
  if (state === 'low_power') return 'Low Power';
  if (state?.toLowerCase() === 'error') {
    return healingInProgress ? 'Issue Detected' : 'Error';
  }
  return state ?? 'Unknown';
}

export function StatusLine() {
  const state = useRobotStore((s) => s.stateMachineState);
  const baseOwner = useRobotStore((s) => s.baseOwner);
  const navStatus = useRobotStore((s) => s.navStatus);
  const healingInProgress = useRobotStore((s) => s.healingInProgress);

  const isError = state?.toLowerCase() === 'error';
  const valueColor =
    state === 'navigation' && baseOwner === 'navigation' && navStatus === 'active'
      ? 'text-success'
      : isError && healingInProgress
        ? 'text-orange-500'
        : isError
          ? 'text-danger'
          : 'text-foreground';

  const label = deriveStateLabel(state, baseOwner, navStatus, healingInProgress);

  return (
    <div className="text-sm flex items-center justify-between gap-3">
      <span className="text-muted">Status</span>
      <span className={`font-semibold text-right ${valueColor}`}>{label}</span>
    </div>
  );
}

export function PositionLine() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const { dockStatus, dockStation } = useMemo(() => {
    const navHb = heartbeats.find((hb) => hb.heartbeat_id === 'navigation');
    const payload = (navHb?.payload ?? {}) as {
      dock_status?: string;
      dock_station?: string;
    };
    return {
      dockStatus: payload.dock_status ?? null,
      dockStation: payload.dock_station ?? null,
    };
  }, [heartbeats]);

  const flashing = dockStatus === 'docking' || dockStatus === 'undocking';
  let label: string = '-';
  if (dockStatus === 'undocking') label = 'Undocking';
  else if (dockStatus === 'docking') label = 'Docking';
  else if (dockStatus === 'docked') {
    label = dockStation ? `Docked (${dockStation})` : 'Docked';
  }

  const valueColor = flashing
    ? 'text-orange-500'
    : dockStatus === 'docked'
      ? 'text-success'
      : 'text-foreground';

  return (
    <div className="text-sm flex items-center justify-between gap-3">
      <span className="text-muted">Position</span>
      <span className={`font-semibold text-right ${valueColor} ${flashing ? 'animate-pulse' : ''}`}>
        {label}
      </span>
    </div>
  );
}

export function StorageModeLine() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const { mode, usbMounted } = useMemo(() => {
    const recHb = heartbeats.find((hb) => hb.heartbeat_id === 'recording_service');
    const payload = (recHb?.payload ?? {}) as {
      storage_mode?: string;
      usb_mounted?: boolean;
    };
    return { mode: payload.storage_mode ?? null, usbMounted: payload.usb_mounted ?? false };
  }, [heartbeats]);

  if (mode == null) return null;
  const missingUsb = mode === 'usb' && !usbMounted;
  const label = mode === 'ssd' ? 'SSD' : missingUsb ? 'No USB stick!' : 'USB stick';
  const valueColor = missingUsb ? 'text-danger' : 'text-foreground';

  return (
    <div className="text-sm flex items-center justify-between gap-3">
      <span className="text-muted">Storage</span>
      <span className={`font-semibold text-right ${valueColor} ${missingUsb ? 'animate-pulse' : ''}`}>
        {label}
      </span>
    </div>
  );
}

function formatHoursMinutes(hrs: number): string {
  const totalMin = Math.max(0, Math.round(hrs * 60));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function BatteryLine() {
  const battery = useRobotStore((s) => s.battery);
  if (battery == null) {
    return (
      <div className="text-sm flex items-center justify-between gap-3">
        <span className="text-muted">Battery</span>
        <span className="font-semibold text-foreground text-right">Using Wall Power</span>
      </div>
    );
  }
  const pct = battery.battery_pct ?? null;
  const isCharging = battery.charging ?? false;
  if (pct == null) {
    return (
      <div className="text-sm flex items-center justify-between gap-3">
        <span className="text-muted">Battery</span>
        <span className="font-semibold text-muted text-right">N/A</span>
      </div>
    );
  }
  const clamped = Math.max(0, Math.min(100, pct));
  const isLow = clamped <= 20;
  const valueColor = isLow && !isCharging ? 'text-danger' : 'text-foreground';
  const subLabel =
    isCharging && battery.time_to_full_hrs != null
      ? `(${formatHoursMinutes(battery.time_to_full_hrs)} to full)`
      : !isCharging && battery.remaining_output_hrs != null
        ? `(${formatHoursMinutes(battery.remaining_output_hrs)} remaining)`
        : null;

  return (
    <div className="text-sm flex items-center justify-between gap-3">
      <span className="text-muted">Battery</span>
      <span className="flex items-center gap-2">
        <span className={`font-semibold tabular-nums ${valueColor}`}>{clamped}%</span>
        {subLabel && <span className="text-xs text-muted">{subLabel}</span>}
      </span>
    </div>
  );
}

interface ContactPopoverProps {
  trigger: (props: { onClick: () => void; open: boolean }) => ReactNode;
  placement?: 'below' | 'above';
}

export function ContactPopover({ trigger, placement = 'below' }: ContactPopoverProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    const id = window.setTimeout(() => setMounted(false), 180);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const isAbove = placement === 'above';

  return (
    <div ref={ref} className="relative">
      {trigger({ onClick: () => setOpen((v) => !v), open })}
      {mounted && (
        <div
          className={`absolute left-1/2 z-20 w-64 transition-all duration-150 ease-out ${
            isAbove ? 'bottom-full mb-2 origin-bottom' : 'top-full mt-2 origin-top'
          } ${
            shown
              ? 'opacity-100 scale-100 -translate-x-1/2 translate-y-0'
              : `opacity-0 scale-90 -translate-x-1/2 ${isAbove ? 'translate-y-1' : '-translate-y-1'}`
          }`}
        >
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-surface ${
              isAbove
                ? '-bottom-1.5 border-r border-b border-border'
                : '-top-1.5 border-l border-t border-border'
            }`}
            aria-hidden
          />
          <div className="relative rounded-lg border border-border bg-surface shadow-lg p-4 flex items-center gap-3">
            <div className="shrink-0 w-12 h-12 rounded-full bg-surface-strong border border-border flex items-center justify-center overflow-hidden">
              <VerneMark aria-hidden className="h-6 w-auto" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-foreground truncate">Verne Support</span>
              <a
                href="tel:+14133365819"
                className="text-sm text-muted hover:text-foreground tabular-nums"
              >
                (413) 336-5819
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HelpButton() {
  return (
    <ContactPopover
      placement="above"
      trigger={({ onClick }) => (
        <button
          onClick={onClick}
          className="flex-1 rounded-md border border-border bg-surface/40 px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-strong transition flex items-center justify-center gap-2"
        >
          <VerneMark aria-hidden className="h-5 w-auto" />
          Help
        </button>
      )}
    />
  );
}

function PowerOffButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Power off the robot"
      aria-label="Power off the robot"
      className="flex-1 rounded-md border border-border bg-surface/40 px-4 py-3 text-foreground hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-500 transition flex items-center justify-center"
    >
      <Power size={20} />
    </button>
  );
}

function QuickActions() {
  const state = useRobotStore((s) => s.stateMachineState);
  const navAvailable = useRobotStore((s) => s.navAvailable);
  const runAction = useActionError((s) => s.runAction);
  const [startingNav, setStartingNav] = useState(false);
  const [startingTask, setStartingTask] = useState(false);

  const cardClass =
    'rounded-md border border-border bg-surface/40 p-3 flex flex-col gap-4';
  const titleClass = 'text-[11px] uppercase tracking-wider text-muted';
  const buttonClass =
    'w-full rounded-md bg-surface-strong px-4 py-3 text-base font-semibold text-foreground hover:bg-surface-muted transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-surface-strong';

  if (state === 'verne_control') {
    return (
      <div className={cardClass}>
        <span className={titleClass}>Verne Control</span>
        <p className="text-xs text-muted leading-relaxed">
          An operator at Verne is controlling the robot remotely. Feel free to contact the
          operator using the button below for questions or concerns.
        </p>
        <ContactPopover
          trigger={({ onClick }) => (
            <button
              onClick={onClick}
              className="w-full rounded-md border border-border bg-surface-muted px-4 py-3 text-base font-semibold text-muted hover:bg-surface-strong transition"
            >
              Contact Operator
            </button>
          )}
        />
      </div>
    );
  }

  const inNavigation = state === 'navigation';
  const inTaskExecution = state === 'task_execution';

  return (
    <div className={cardClass}>
      <span className={titleClass}>Quick Actions</span>
      {navAvailable && (
        <button
          disabled={startingNav || inNavigation}
          onClick={() => {
            setStartingNav(true);
            runAction(startNavigation).finally(() => setStartingNav(false));
          }}
          className={buttonClass}
        >
          <Navigation2 size={16} className="rotate-0" />
          {startingNav ? 'Starting…' : 'Navigate'}
        </button>
      )}
      <button
        disabled={startingTask || inTaskExecution}
        onClick={() => {
          setStartingTask(true);
          runAction(startTaskExecution).finally(() => setStartingTask(false));
        }}
        className={buttonClass}
      >
        <Play size={16} />
        {startingTask ? 'Starting…' : 'Start Task'}
      </button>
    </div>
  );
}

export function DashboardSidebar({
  mapInfo,
  mapUrl,
  mapLoading,
  navPose,
  navPath,
  waypoints,
  onOpenMap,
}: DashboardSidebarProps) {
  const navAvailable = useRobotStore((s) => s.navAvailable);
  const [powerOffOpen, setPowerOffOpen] = useState(false);

  return (
    <aside className="h-full w-[300px] shrink-0 flex flex-col gap-4 p-3 border-r border-border bg-surface/40">
      <div className="flex items-center justify-center">
        {mapInfo && navAvailable ? (
          <RobotMinimap
            mapInfo={mapInfo}
            mapUrl={mapUrl}
            robotPose={navPose}
            path={navPath}
            waypoints={waypoints}
            onClick={onOpenMap}
          />
        ) : mapLoading ? (
          <div className="w-[280px] h-[280px] rounded-2xl border border-border bg-surface flex items-center justify-center">
            <Loader2 size={64} className="animate-spin text-accent" />
          </div>
        ) : (
          <div className="w-[280px] h-[280px] rounded-2xl border border-border bg-surface flex items-center justify-center text-xs text-muted">
            Map unavailable
          </div>
        )}
      </div>

      <QuickActions />

      <div className="rounded-md border border-border bg-surface/40 p-3 flex flex-col gap-4">
        <span className="text-[11px] uppercase tracking-wider text-muted">Robot Status</span>
        <div className="flex flex-col gap-1.5">
          <StatusLine />
          <PositionLine />
          <BatteryLine />
          <StorageModeLine />
        </div>
      </div>

      <div className="mt-auto flex items-stretch gap-2">
        <HelpButton />
        <PowerOffButton onClick={() => setPowerOffOpen(true)} />
      </div>

      {powerOffOpen && <PowerOffOverlay onCancel={() => setPowerOffOpen(false)} />}
    </aside>
  );
}
